import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import moment from 'moment';
import PropTypes from 'prop-types';

import { NODE_DATA_MODEL } from '../../../models/CertsModels';
import { getAuditHistoryData } from '../../../redux/certdetails/action';
import { SANITIZED_DATE_FORMATS_MAPPING } from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { CERT_ENTITLEMENT_VALUEKEY } from '../constant';

import { invertedMessages } from './messages';

import './CertsConsultedActionPopover.css';

function CertsConsultedContent({
  isCommentPopoverOpen,
  onClose,
  resetAction,
  nodeData,
  certKey,
  globalDateFormat,
}) {
  const [isLoadingActionDetails, setIsLoadingActionDetails] = useState(true);
  const [latestActionDetails, setLatestActionDetails] = useState(null);
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);

  const fetchAuditHistory = async () => {
    const body = {
      certkey: certKey,
      cert_entitlement_valuekey: nodeData?.CERT_ENTITLEMENT_VALUEKEY,
      cert_userkey: nodeData?.CERT_USERKEY,
    };

    switch (nodeData?.CERT_ENTITLEMENT_VALUEKEY) {
      case CERT_ENTITLEMENT_VALUEKEY.USER:
        break;
      case CERT_ENTITLEMENT_VALUEKEY.ACCOUNT:
        if (nodeData.CERT_ACCOUNTKEY) {
          body.cert_accountkey = nodeData.CERT_ACCOUNTKEY;
        }

        break;
      case CERT_ENTITLEMENT_VALUEKEY.ROLE:
        body.cert_rolekey = nodeData.CERT_ROLEKEY;

        break;
      case CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT:
      default:
        if (CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT >= 0) {
          if (nodeData.CERT_ACCOUNTKEY) {
            body.cert_accountkey = nodeData.CERT_ACCOUNTKEY;
          }

          if (nodeData?.CERT_ROLEKEY) {
            body.cert_rolekey = nodeData.CERT_ROLEKEY;
          }
        }

        break;
    }

    dispatch(getAuditHistoryData(body))
      .then((data) => {
        const { response } = data;

        if (Array.isArray(response) && response.length > 0) {
          let firstNonConsultedAction = null;

          response.forEach((ele) => {
            if (ele.action !== 'consulted' && !firstNonConsultedAction) {
              firstNonConsultedAction = ele;
            }
          });

          // storing non consulted action
          if (firstNonConsultedAction) {
            setLatestActionDetails(firstNonConsultedAction);
          }
        } else {
          const details = {
            errorMsg: l('Network Error , Please retry after sometime'),
          };

          setLatestActionDetails(details);
        }
      })
      .catch(() => {
        const details = {
          errorMsg: l('Network Error , Please retry after sometime'),
        };

        setLatestActionDetails(details);
      })
      .finally(() => {
        setIsLoadingActionDetails(false);
      });
  };

  useEffect(() => {
    if (isCommentPopoverOpen) {
      fetchAuditHistory();

      return;
    }

    setIsLoadingActionDetails(true);
  }, [isCommentPopoverOpen]);

  const getIconKind = () => {
    switch (latestActionDetails?.action) {
      case 'reassigned':
        return 'ReassignUser';
      case 'consulted':
      case 'conditionallyCertified':
        return 'Consult';
      case 'rejected':
        return 'Reject';
      case 'approved':
      case 'cancelled':
        return 'CheckCircleOutline';
      case 'worksForMe':
      default:
        return 'Success';
    }
  };

  const getIconColor = () => {
    switch (latestActionDetails?.action) {
      case 'reassigned':
        return 'navy-900';
      case 'consulted':
        return 'green-900';
      case 'conditionallyCertified':
        return 'success-900';
      case 'rejected':
        return 'warning-900';
      case 'approved':
        return 'success-900';
      case 'worksForMe':
        return 'success-700';
      case 'cancelled':
        return 'warning-700';
      default:
        return 'success-700';
    }
  };

  const getActionType = (type) => {
    if (type !== '') {
      return type;
    }

    return 'NOT_PROVIDED';
  };

  const renderActionResetContent = () => {
    if (isLoadingActionDetails) {
      return (
        <Box className='CertsConsultedActionPopover-loader'>
          <Loader kind='dots' format='inline' color='brand' size='small' />
        </Box>
      );
    }

    if (latestActionDetails.errorMsg) {
      return (
        <Box className='CertsConsultedActionPopover-errorMsg'>
          <Typography kind='body2-bold'>
            {latestActionDetails?.errorMsg}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box className='CertsConsultedActionPopover-content'>
          <Box className='CertsConsultedActionPopover-header'>
            <Box className='CertsConsultedActionPopover-actionStatus'>
              <Icon kind={getIconKind()} color={getIconColor()} />
              <Typography
                kind='h3'
                desktop='h3-d'
                mobile='h3-d'
                className='CertsConsultedActionPopover-title'>
                {getActionType(latestActionDetails?.actionMessage)}
              </Typography>
            </Box>
            <ButtonIcon
              size='small'
              kind='ghost'
              icon={<Icon kind='Close' color='neutral-1000' />}
              onClick={onClose}
            />
          </Box>
          <Box kind='body1' className='CertsConsultedActionPopover-details'>
            <Box className='CertsConsultedActionPopover-detailsSection'>
              <Typography
                kind='body3'
                className='CertsConsultedActionPopover-heading'>
                {l('Actioned By')}
              </Typography>
              <Typography
                kind='body2'
                color='--color-foreground-secondary-bold'
                lineLimit={2}>
                {latestActionDetails?.actionUser}
              </Typography>
            </Box>
            <Box>
              <Typography
                kind='body3'
                className='CertsConsultedActionPopover-heading'>
                {l('Actioned On')}
              </Typography>
              <Typography
                kind='body2'
                color='--color-foreground-secondary-bold'
                lineLimit={2}>
                {latestActionDetails?.actionDate &&
                  moment
                    .utc(
                      latestActionDetails?.actionDate,
                      SANITIZED_DATE_FORMATS_MAPPING?.[
                        latestActionDetails?.actionDateFormat
                      ]
                    )
                    .local()
                    .format(`${globalDateFormat?.toUpperCase()} hh:mm:ss`)}
              </Typography>
            </Box>
          </Box>
          <Box className='CertsConsultedActionPopover-commentsInfo'>
            <Typography
              kind='body3'
              className='CertsConsultedActionPopover-heading'>
              {l('Comments')}
            </Typography>
            <Tooltip
              openDelay={500}
              text={
                latestActionDetails?.comment?.length > 100
                  ? latestActionDetails?.comment
                  : null
              }
              maxWidth='20rem'
              placement='bottom'
              offset={8}
              trigger={
                <Typography
                  kind='body2'
                  color='--color-foreground-secondary-bold'
                  lineLimit={2}>
                  {latestActionDetails?.comment || '-'}
                </Typography>
              }
            />
          </Box>
        </Box>
        {/* Buttons */}
        <Box tag='div' className='CertsConsultedActionPopover-controls'>
          <Button
            type='button'
            kind='subtle'
            onClick={() => {
              resetAction();
            }}>
            {l('Cancel Action')}
          </Button>
          <Button type='button' kind='filled' onClick={onClose}>
            {l('Got it')}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box className='CertsConsultedActionPopover'>
      {renderActionResetContent()}
    </Box>
  );
}

CertsConsultedContent.defaultProps = {};

CertsConsultedContent.propTypes = {
  isCommentPopoverOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  resetAction: PropTypes.func.isRequired,
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  certKey: PropTypes.string.isRequired,
  globalDateFormat: PropTypes.string.isRequired,
};

export default CertsConsultedContent;
